import Alpine from 'alpinejs'
import { initCookieConsent } from './cookie-consent'
import { initFormConversions } from './form-conversions'
import { initContactAnalytics } from './analytics'
import ticketBookingForm from './TicketBooking/TicketBooking'

void initCookieConsent()
initFormConversions()
initContactAnalytics()

function bindAnchorOffset() {
    const header = document.querySelector<HTMLElement>('header.wp-block-template-part')
    if (!header) return

    let pendingAnchor: HTMLElement | null = null
    const prepareAnchor = (hash: string) => {
        let id: string
        try {
            id = decodeURIComponent(hash.slice(1))
        } catch {
            return
        }
        const target = document.getElementById(id)
        if (!target) return
        pendingAnchor = target
        for (let element: HTMLElement | null = target; element; element = element.parentElement) {
            if (!element.classList.contains('has-scroll-reveal')) continue
            element.classList.remove('is-reveal-ready', 'is-reveal-visible', 'is-reveal-arming')
            element.dataset.lynReveal = 'complete'
        }
    }

    const update = () => {
        const styles = getComputedStyle(header)
        const fixed = styles.position === 'sticky' || styles.position === 'fixed'
        const offset = fixed ? header.getBoundingClientRect().height + (parseFloat(styles.top) || 0) : 0
        document.documentElement.style.setProperty('--avianese-header-offset', `${offset}px`)
    }

    update()
    new ResizeObserver(update).observe(header)
    window.addEventListener('resize', update)
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 48)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    document.addEventListener('click', event => {
        if (!(event instanceof MouseEvent) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        const link = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a[href]') : null
        if (!link || link.target === '_blank') return
        const url = new URL(link.href, location.href)
        if (url.origin === location.origin && url.pathname === location.pathname && url.search === location.search && url.hash) prepareAnchor(url.hash)
    }, { capture: true })
    window.addEventListener('hashchange', () => prepareAnchor(location.hash))
    const alignAnchor = () => {
        if (!pendingAnchor) return
        update()
        const target = pendingAnchor
        pendingAnchor = null
        const padding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0
        if (Math.abs(target.getBoundingClientRect().top - padding) > 1) {
            target.scrollIntoView({ block: 'start', behavior: 'instant' })
        }
    }
    window.addEventListener('scrollend', alignAnchor)
    header.addEventListener('transitionend', alignAnchor)
    if (location.hash) {
        prepareAnchor(location.hash)
        if (document.readyState === 'complete') requestAnimationFrame(alignAnchor)
        else window.addEventListener('load', () => requestAnimationFrame(alignAnchor), { once: true })
    }
}

bindAnchorOffset()

declare global {
    interface Window {
        Alpine: typeof Alpine
        algoliaAppId: string
        algoliaSearchOnlyApiKey: string
    }
}

function swipeCloseMenu() {
    let startX = 0

    document.addEventListener('touchstart', e => {
        startX = e.touches[0].clientX
    })

    document.addEventListener('touchend', e => {
        const endX = e.changedTouches[0].clientX
        if (endX - startX > 50) { // swipe right
            const navStore = Alpine.store('nav_menu') as {
                shown: boolean
                toggle: () => void
            }
            if (navStore.shown) {
                navStore.toggle()
            }
        }
    })
}

function normalizeText(text: string): string {
    return text.replace(/[→↗]/g, '').replace(/\s+/g, ' ').trim().toLowerCase()
}

function openQuoteModal(): boolean {
    const data = Alpine.store('data') as {
        showModal?: boolean,
        showQuoteDetails?: boolean,
        quoteSubmitted?: boolean,
        quoteFailureVisible?: boolean,
        quoteFailureMessage?: string,
    } | undefined

    if (!data) return false

    data.showQuoteDetails = false
    data.quoteSubmitted = false
    data.quoteFailureVisible = false
    data.quoteFailureMessage = ''
    data.showModal = true
    document.body.style.overflow = 'hidden'

    return true
}

function isQuoteLink(target: HTMLElement): boolean {
    const link = target.closest<HTMLAnchorElement>('a[href]')

    if (!link) return false

    try {
        return new URL(link.href, window.location.href).hash.toLowerCase() === '#quote'
    } catch {
        return false
    }
}

function bindQuoteTriggers() {
    document.addEventListener('click', event => {
        if (!(event.target instanceof Element)) return

        const explicitTrigger = event.target.closest<HTMLElement>('.ticket-modal-trigger, [data-quote-open]')
        const target = explicitTrigger
            ?? event.target.closest<HTMLElement>('a, button, [role="button"]')

        if (!target) return

        const isQuoteTrigger = explicitTrigger !== null
            || isQuoteLink(target)
            || normalizeText(target.textContent || '') === 'get a quote'

        if (!isQuoteTrigger || !openQuoteModal()) return

        event.preventDefault()
    }, { capture: true })
}

function bindTrustCounters() {
    const strip = Array.from(document.querySelectorAll<HTMLElement>('.wp-block-wpvb-container'))
        .filter(element => {
            const text = normalizeText(element.textContent || '')
            return text.includes('operators vetted to')
                && text.includes('8,000+')
                && text.includes('40,000+')
                && text.includes('24/7')
        })
        .sort((left, right) => left.textContent!.length - right.textContent!.length)[0]

    if (!strip) return

    const targets = [
        { text: '8,000+', value: 8000 },
        { text: '40,000+', value: 40000 },
    ]
    const counters = targets.flatMap(target => {
        const element = Array.from(strip.querySelectorAll<HTMLElement>('p'))
            .find(candidate => candidate.textContent?.trim() === target.text)
        return element ? [{ element, value: target.value }] : []
    })

    if (!counters.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const run = ({ element, value }: typeof counters[number]) => {
        const start = performance.now()
        const duration = 2800

        const tick = (now: number) => {
            const progress = Math.min((now - start) / duration, 1)
            const current = Math.round(value * (1 - Math.pow(1 - progress, 4)))
            element.textContent = `${current.toLocaleString('en-US')}+`

            if (progress < 1) requestAnimationFrame(tick)
        }

        requestAnimationFrame(tick)
    }

    if (!('IntersectionObserver' in window)) {
        counters.forEach(run)
        return
    }

    const observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return
        counters.forEach(run)
        observer.disconnect()
    }, { threshold: 0.6 })

    observer.observe(strip)
}

// Module scripts are deferred — DOM is ready when this executes
window.Alpine = Alpine
window.algoliaAppId = import.meta.env.VITE_ALGOLIA_APP_ID as string
window.algoliaSearchOnlyApiKey = import.meta.env.VITE_ALGOLIA_SEARCH_ONLY_API_KEY as string

document.addEventListener('alpine:init', () => {
    ticketBookingForm()

    Alpine.data('savingsTool', () => ({
        spend: 500,
        markup: 10,
        get totalSpend() {
            return this.spend * 1000
        },
        get operatorCost() {
            return this.totalSpend / (1 + this.markup / 100)
        },
        get brokerMargin() {
            return this.totalSpend - this.operatorCost
        },
        formatCurrency(value: number) {
            return `$${Math.round(value).toLocaleString('en-US')}`
        },
    }))

    const navMenu = {
        shown: false,
        toggle() {
            this.shown = !this.shown
        }
    }
    Alpine.store('nav_menu', navMenu)

    const MARKER_TRANSITION = 'transform 200ms ease-out, width 200ms ease-out'

    const navMarker = {
        width: 0,
        left: 0,
        transition: MARKER_TRANSITION,
        container: null as HTMLElement | null,
        setContainer(el: HTMLElement) {
            this.container = el
        },
        isDesktop() {
            return window.matchMedia('(min-width: 1024px)').matches
        },
        getRects(link: HTMLElement) {
            if (!this.container || !link) return null
            const containerRect = this.container.getBoundingClientRect()
            const linkRect = link.getBoundingClientRect()
            return { containerRect, linkRect }
        },
        setFromItem(item: HTMLElement | null, options: { animate?: boolean } = {}) {
            if (!item || !this.isDesktop()) return
            const link = item.querySelector('.lyn-nav-dropdown') || item.querySelector('.lyn-nav-link')
            if (!link) return
            const { animate = true } = options
            const rects = this.getRects(link as HTMLElement)
            if (!rects) return

            this.width = rects.linkRect.width
            this.left = rects.linkRect.left - rects.containerRect.left
            this.transition = animate ? MARKER_TRANSITION : 'none'
        },
        reset() {
            this.width = 0
            this.transition = MARKER_TRANSITION
        },
        get style() {
            return {
                '--lyn-nav-marker-width': `${this.width}px`,
                '--lyn-nav-marker-left': `${this.left}px`,
                '--lyn-nav-marker-transition': this.transition,
            }
        },
    }
    Alpine.store('nav_marker', navMarker)
})

swipeCloseMenu()
bindQuoteTriggers()
bindTrustCounters()

// Workaround for Bitwarden extension bug that throws when inputs lack autocomplete
// https://github.com/bitwarden/clients/issues/19536
const patchAutoComplete = () =>
    document.querySelectorAll<HTMLInputElement>('input:not([autocomplete])').forEach(el => {
        el.setAttribute('autocomplete', 'off')
    })
patchAutoComplete()
const acObserver = new MutationObserver(patchAutoComplete)
acObserver.observe(document.body, { childList: true, subtree: true })
setTimeout(() => acObserver.disconnect(), 3000)

function getFluentFormElement(form: unknown): HTMLFormElement | null {
    if (form instanceof HTMLFormElement) {
        return form
    }

    if (form && typeof form === 'object' && '0' in form) {
        const element = (form as { 0?: unknown })[0]
        return element instanceof HTMLFormElement ? element : null
    }

    return null
}

function completeQuoteSubmission(form: unknown, response?: { data?: { result?: { redirectUrl?: string } } }) {
    const formElement = getFluentFormElement(form)
    if (!formElement?.closest('.modal.quote-form')) return
    if (response?.data?.result?.redirectUrl) return

    jQuery('.modal-scroll-box').removeClass('overflow-y-auto')
    const data = Alpine.store('data') as { completeSubmission?: () => void } | undefined
    data?.completeSubmission?.()
}

jQuery(document.body).on('fluentform_submission_success', function (_event, payload) {
    completeQuoteSubmission(payload?.form, payload?.response)
})

document.addEventListener('fluentform_submission_success', event => {
    const detail = event instanceof CustomEvent ? event.detail : null
    completeQuoteSubmission(detail?.form, detail?.response)
})

jQuery(document.body).on('fluentform_submission_failed', function (_event, payload) {
    const formElement = getFluentFormElement(payload?.form)
    if (!formElement?.closest('.modal.quote-form')) return

    const data = Alpine.store('data') as { failSubmission?: (message?: string) => void } | undefined
    data?.failSubmission?.()
})

document.addEventListener('fluentform_submission_failed', event => {
    const detail = event instanceof CustomEvent ? event.detail : null
    const formElement = getFluentFormElement(detail?.form)
    if (!formElement?.closest('.modal.quote-form')) return

    const data = Alpine.store('data') as { failSubmission?: (message?: string) => void } | undefined
    data?.failSubmission?.()
})

jQuery(document).on('fluentform_validation_failed', '#fluentform_1', function () {
    const formElement = this instanceof HTMLFormElement ? this : null
    if (!formElement?.closest('.modal.quote-form')) return

    const data = Alpine.store('data') as { failSubmission?: (message?: string) => void } | undefined
    data?.failSubmission?.('Please check the highlighted fields. If the form still will not send, contact us directly below.')
})

document.querySelectorAll('.quote-form-host').forEach(host => document.body.append(host))
Alpine.start()

// Hot Module Logic
if ((import.meta as any).hot) {
    (import.meta as any).hot.on('vite:beforeFullReload', () => {
        throw '(skipping full reload)'
    })
}
